import type {
  DashboardStats,
  RevenueDataPoint,
  CustomerGrowthDataPoint,
  AppStatus,
  QueueItem,
  Settings,
  AdminUser,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://sggadgets-backend.aadhithyanannamalai.workers.dev';

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...getHeaders(),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

export async function login(email: string, password: string): Promise<{ token: string; user: AdminUser }> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Invalid credentials' } }));
    throw new Error(error.error?.message || 'Invalid credentials');
  }

  const data = await response.json() as { success: boolean; data: { accessToken: string; user: AdminUser } };
  
  localStorage.setItem('admin_token', data.data.accessToken);
  
  return { token: data.data.accessToken, user: data.data.user };
}

export async function logout(): Promise<void> {
  localStorage.removeItem('admin_token');
}

export async function getStats(): Promise<DashboardStats> {
  return fetchApi<DashboardStats>('/admin/stats');
}

export async function getRevenueData(period: string): Promise<RevenueDataPoint[]> {
  return fetchApi<RevenueDataPoint[]>(`/admin/stats/revenue?period=${period}`);
}

export async function getCustomerGrowthData(period: string): Promise<CustomerGrowthDataPoint[]> {
  return fetchApi<CustomerGrowthDataPoint[]>(`/admin/stats/customers?period=${period}`);
}

export async function getAppsStatus(): Promise<AppStatus[]> {
  return fetchApi<AppStatus[]>('/admin/apps/status');
}

export async function triggerAppAction(appName: string, action: string): Promise<{ success: boolean; message: string }> {
  return fetchApi<{ success: boolean; message: string }>(`/admin/apps/${appName}/${action}`, {
    method: 'POST',
  });
}

export async function getAppLogs(appName: string, limit: number): Promise<string[]> {
  return fetchApi<string[]>(`/admin/apps/${appName}/logs?limit=${limit}`);
}

export async function getUploadQueue(page: number, limit: number): Promise<{ items: QueueItem[]; total: number; page: number }> {
  return fetchApi<{ items: QueueItem[]; total: number; page: number }>(`/admin/upload-queue?page=${page}&limit=${limit}`);
}

export async function approveQueueItem(itemId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/upload-queue/${itemId}/approve`, {
    method: 'POST',
  });
}

export async function rejectQueueItem(itemId: string, reason: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/upload-queue/${itemId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function updateQueueItem(itemId: string, data: Partial<QueueItem>): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/upload-queue/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function publishQueueItem(itemId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/upload-queue/${itemId}/publish`, {
    method: 'POST',
  });
}

export async function getSettings(): Promise<Settings> {
  return fetchApi<Settings>('/admin/settings');
}

export async function updateSettings(data: Partial<Settings>): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function createApiKey(name: string): Promise<{ success: boolean; key: string }> {
  return fetchApi<{ success: boolean; key: string }>('/admin/settings/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function revokeApiKey(keyId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/settings/api-keys/${keyId}`, {
    method: 'DELETE',
  });
}

export async function updateAdminUser(userId: string, data: Partial<AdminUser>): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`/admin/settings/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function runHealthCheck(appName: string): Promise<{ status: string; details: Record<string, unknown> }> {
  return fetchApi<{ status: string; details: Record<string, unknown> }>(`/admin/apps/${appName}/health`);
}
